/**
 * Safe localStorage / sessionStorage helpers.
 *
 * Wraps `JSON.parse(localStorage.getItem(...))` so corrupted cache (from a
 * previous version of the app, a partial write, or browser-extension
 * tampering) returns the fallback instead of crashing the page on mount.
 *
 *   import { getJSON, setJSON, getString, setString, remove } from '../lib/storage';
 *
 *   const user = getJSON('user', null);   // never throws
 *   setJSON('user', { id: 1, ... });
 *
 * Also `typeof window` guards the storage access so any pre-render path
 * (SSR, static export, tests in jsdom-less environments) stays safe.
 */
function _store(area) {
  try {
    if (typeof window === 'undefined') return null;
    return area === 'session' ? window.sessionStorage : window.localStorage;
  } catch { return null; }
}

export function getJSON(key, fallback = null, area = 'local') {
  const s = _store(area);
  if (!s) return fallback;
  try {
    const raw = s.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw);
  } catch {
    // Corrupted JSON — clear it so the next read doesn't hit the same wall.
    try { s.removeItem(key); } catch {}
    return fallback;
  }
}

export function setJSON(key, value, area = 'local') {
  const s = _store(area);
  if (!s) return false;
  try {
    s.setItem(key, JSON.stringify(value));
    return true;
  } catch { return false; } // QuotaExceededError, private mode, etc.
}

export function getString(key, fallback = '', area = 'local') {
  const s = _store(area);
  if (!s) return fallback;
  try { return s.getItem(key) ?? fallback; } catch { return fallback; }
}

export function setString(key, value, area = 'local') {
  const s = _store(area);
  if (!s) return false;
  try { s.setItem(key, value); return true; } catch { return false; }
}

export function remove(key, area = 'local') {
  const s = _store(area);
  if (!s) return;
  try { s.removeItem(key); } catch {}
}
